import { FC } from 'react';
import { Car } from '../../../../types/car';
import styles from './styles.module.css';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import TableMenuButton from '../TableMenuButton';
import { CarFormDataProviderProps } from '../EditSideBar';
import PolicyRequired from '../../../../components/common/PolicyRequired';
import { getNumberString } from '../../../../services/getNumberString';
import { format } from 'date-fns';

/** 編集・削除ボタンのためのカラムを表示するのに必要なポリシー (いずれがあれば表示) */
const etcColumnRequiredPolicies = [14, 15] as const;

type Props = {
  cars: Car[];
  openEdit: (props: CarFormDataProviderProps | null) => void;
  sidebarClose: () => void;
};

const CarsTable: FC<Props> = ({ cars, openEdit, sidebarClose }) => {
  const isUnavailable = (
    startDate: string | null | undefined,
    endDate: string | null | undefined
  ) => {
    const now = new Date();
    const start = startDate ? new Date(startDate) : undefined;
    const end = endDate ? new Date(endDate) : undefined;
    if (
      !(
        (start || end) &&
        (!start || start.setHours(0, 0, 0, 0) <= now.setHours(0, 0, 0, 0)) &&
        (!end || now.setHours(0, 0, 0, 0) <= end.setHours(0, 0, 0, 0))
      )
    )
      return (
        <div className={styles.isAvailableMark}>
          <FontAwesomeIcon icon={faCircle} />
        </div>
      );

    const str = (t: Date | undefined) => (t ? format(t, 'M/d') : '');
    return (
      <div className={styles.isUnavailableMark}>
        <FontAwesomeIcon icon={faXmark} />
        <div className={styles.isUnavailableDate}>
          {str(start)}-{str(end)}
        </div>
      </div>
    );
  };

  return (
    <table className={classNames(styles.table, 'rounded-corners')}>
      <thead>
        <tr className={styles.head}>
          <th className={classNames(styles.headCell, styles.carCategory)}>
            車両カテゴリ
          </th>
          <th className={classNames(styles.headCell, styles.carNo)}>車両No</th>
          <th className={classNames(styles.headCell, styles.maxWeight)}>
            積載上限重量(kg)
          </th>
          <th className={classNames(styles.headCell, styles.maxVolume)}>
            積載上限重量(㎥)
          </th>
          <th className={classNames(styles.headCell, styles.driverLicense)}>
            免許種別
          </th>
          <th
            className={classNames(
              styles.headCell,
              styles.isAvailable,
              styles.center
            )}
          >
            利用
          </th>
          <PolicyRequired anyPolicy={etcColumnRequiredPolicies}>
            <th className={classNames(styles.headCell, styles.etc)}></th>
          </PolicyRequired>
        </tr>
      </thead>
      <tbody>
        {cars.map((c) => (
          <tr key={c.carId}>
            <td className={styles.dataCell}>
              <div className={styles.carCategoryNameText}>
                {c.carCategory.name}
              </div>
            </td>
            <td className={styles.dataCell}>
              <div className={styles.carNoText}>{c.carNo}</div>
            </td>
            <td className={styles.dataCell}>{getNumberString(c.maxWeight)}</td>
            <td className={styles.dataCell}>{getNumberString(c.maxVolume)}</td>
            <td className={styles.dataCell}>
              <div key={c.driverLicense.id}>{c.driverLicense.name}</div>
            </td>
            <td className={styles.dataCell}>
              {isUnavailable(c.unavailableStartDate, c.unavailableEndDate)}
            </td>
            <PolicyRequired anyPolicy={etcColumnRequiredPolicies}>
              <td
                className={classNames(
                  styles.dataCell,
                  styles.ellipsisContainer
                )}
              >
                <TableMenuButton
                  car={c}
                  openEdit={openEdit}
                  sidebarClose={sidebarClose}
                />
              </td>
            </PolicyRequired>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CarsTable;
